import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Copy, ImagePlus, X } from "lucide-react";
import { newRid } from "../lib/rid";
import { getNextSkus } from "../lib/skuUtils";
import { fileToCompressedDataURL } from "../lib/imageUtils";

const MAX_IMAGES = 6;

const emptyRow = (sku = "") => ({
    _rid: newRid(),
    name: "", sku, category: "", supplier: "",
    stock: 0, cost_price: 0, unit_price: 0, low_stock_threshold: 0,
    images: [],
});

// ---------- Inline image cell (per-row compact uploader) ----------
function RowImagesCell({ images, onChange, testIdPrefix }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const list = Array.isArray(images) ? images : [];

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        const room = MAX_IMAGES - list.length;
        if (room <= 0) { toast.error(`最多 ${MAX_IMAGES} 张`); return; }
        const arr = Array.from(files).slice(0, room);
        setBusy(true);
        try {
            const added = [];
            for (const f of arr) {
                if (!f.type.startsWith("image/")) continue;
                added.push(await fileToCompressedDataURL(f));
            }
            if (added.length) onChange([...list, ...added].slice(0, MAX_IMAGES));
        } catch (e) {
            toast.error(e.message || "图片处理失败");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-1.5" data-testid={`${testIdPrefix}-cell`}>
            {list.length === 0 ? (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    data-testid={`${testIdPrefix}-add-btn`}
                    disabled={busy}
                    className="h-14 w-14 shrink-0 rounded-md border-2 border-dashed border-slate-300 bg-gold-50/40 hover:border-gold-400 hover:bg-gold-50 flex flex-col items-center justify-center text-gold-600 transition-colors disabled:opacity-50"
                    title="上传图片"
                >
                    {busy ? (
                        <span className="text-[10px]">处理中</span>
                    ) : (
                        <>
                            <ImagePlus className="h-4 w-4" />
                            <span className="text-[10px] mt-0.5">图片</span>
                        </>
                    )}
                </button>
            ) : (
                <div className="relative group h-14 w-14 shrink-0 rounded-md overflow-hidden border border-slate-200 bg-slate-50">
                    <img src={list[0]} alt="" className="w-full h-full object-cover" />
                    {list.length > 1 && (
                        <div className="absolute bottom-0.5 right-0.5 bg-gold-600 text-white text-[9px] font-bold px-1 rounded-sm leading-tight">
                            +{list.length - 1}
                        </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            data-testid={`${testIdPrefix}-more-btn`}
                            disabled={busy || list.length >= MAX_IMAGES}
                            className="h-6 w-6 rounded-full bg-white/95 text-slate-800 flex items-center justify-center hover:bg-gold-100 disabled:opacity-40"
                            title={list.length >= MAX_IMAGES ? "已达上限" : "再加一张"}
                        >
                            <ImagePlus className="h-3 w-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            data-testid={`${testIdPrefix}-clear-btn`}
                            className="h-6 w-6 rounded-full bg-white/95 text-red-600 flex items-center justify-center hover:bg-red-50"
                            title="清空"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid={`${testIdPrefix}-input`}
                onChange={(e) => { if (e.target.files) { handleFiles(e.target.files); e.target.value = ""; } }}
            />
        </div>
    );
}

export default function BulkAddProductsDialog({ open, onOpenChange, onDone, products = [] }) {
    const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
    const [submitting, setSubmitting] = useState(false);

    // Prefill SKU sequence whenever the dialog is (re)opened
    useEffect(() => {
        if (!open) return;
        const skus = getNextSkus(products, 3);
        setRows([emptyRow(skus[0]), emptyRow(skus[1]), emptyRow(skus[2])]);
    }, [open, products]);

    const validCount = rows.filter((r) => r.name.trim() && r.sku.trim()).length;

    const update = (i, patch) => {
        const next = [...rows];
        next[i] = { ...next[i], ...patch };
        setRows(next);
    };

    const nextSkuFor = (currentRows) => {
        const used = currentRows.map((r) => (r.sku || "").trim()).filter(Boolean);
        return getNextSkus(products, 1, used)[0];
    };

    const addRow = () => setRows((cur) => [...cur, emptyRow(nextSkuFor(cur))]);
    const dupRow = (i) => {
        setRows((cur) => {
            const next = [...cur];
            const src = cur[i];
            next.splice(i + 1, 0, { ...src, _rid: newRid(), sku: nextSkuFor(cur) });
            return next;
        });
    };
    const removeRow = (i) => {
        setRows((cur) => {
            if (cur.length === 1) return [emptyRow(nextSkuFor([]))];
            return cur.filter((_, idx) => idx !== i);
        });
    };

    const reset = () => {
        const skus = getNextSkus(products, 3);
        setRows([emptyRow(skus[0]), emptyRow(skus[1]), emptyRow(skus[2])]);
    };

    const submit = async () => {
        const productsPayload = rows
            .filter((r) => r.name.trim() && r.sku.trim())
            .map((r) => ({
                name: r.name.trim(),
                sku: r.sku.trim(),
                category: r.category.trim(),
                supplier: r.supplier.trim(),
                stock: Number(r.stock) || 0,
                cost_price: Number(r.cost_price) || 0,
                unit_price: Number(r.unit_price) || 0,
                low_stock_threshold: Number(r.low_stock_threshold) || 0,
                images: Array.isArray(r.images) ? r.images.slice(0, MAX_IMAGES) : [],
                image_url: (Array.isArray(r.images) && r.images[0]) || "",
            }));
        if (productsPayload.length === 0) {
            toast.error("请至少填写 1 件产品的名称与 SKU");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/products/bulk", { products: productsPayload });
            toast.success(`成功添加 ${data.created} 件${data.skipped ? `，跳过 ${data.skipped} 件（SKU 重复）` : ""}`);
            reset();
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "添加失败");
        } finally {
            setSubmitting(false);
        }
    };

    // 9 columns: image, name, sku, category, stock, cost, price, threshold, actions
    const gridCols = "grid-cols-2 md:grid-cols-[76px_minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_80px_100px_100px_80px_40px]";

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
            <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="bulk-add-dialog">
                <DialogHeader>
                    <DialogTitle>批量添加产品</DialogTitle>
                    <p className="text-xs text-gold-500 pt-1">
                        一次填写多件产品；只需填 <span className="text-slate-700 font-semibold">名称</span> 与 <span className="text-slate-700 font-semibold">SKU</span> 即可保存 · SKU 会按现有编号自动递增，可手动改
                    </p>
                </DialogHeader>

                <div className="space-y-2 pt-2">
                    <div className={`hidden md:grid ${gridCols} gap-2 px-2 pb-1`}>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold">图片</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold">名称 *</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold">SKU *</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold">分类</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold text-right">库存</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold text-right">成本</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold text-right">售价</div>
                        <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold text-right">阈值</div>
                        <div></div>
                    </div>

                    <div className="space-y-2">
                        {rows.map((r, i) => {
                            const filled = r.name.trim() && r.sku.trim();
                            return (
                                <div
                                    key={r._rid}
                                    data-testid={`bulk-row-${i}`}
                                    className={`grid ${gridCols} gap-2 items-start p-2 rounded-md border ${filled ? "border-gold-200 bg-gold-50/30" : "border-slate-200"}`}
                                >
                                    <div className="col-span-2 md:col-span-1">
                                        <Label className="md:hidden text-xs text-gold-500">图片</Label>
                                        <RowImagesCell
                                            images={r.images}
                                            onChange={(imgs) => update(i, { images: imgs })}
                                            testIdPrefix={`bulk-images-${i}`}
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <Label className="md:hidden text-xs text-gold-500">名称 *</Label>
                                        <Input
                                            data-testid={`bulk-name-${i}`}
                                            value={r.name}
                                            onChange={(e) => update(i, { name: e.target.value })}
                                            placeholder="产品名称"
                                        />
                                    </div>
                                    <div>
                                        <Label className="md:hidden text-xs text-gold-500">SKU *</Label>
                                        <Input
                                            data-testid={`bulk-sku-${i}`}
                                            value={r.sku}
                                            onChange={(e) => update(i, { sku: e.target.value })}
                                            placeholder="SKU"
                                            className="font-mono"
                                        />
                                    </div>
                                    <div>
                                        <Label className="md:hidden text-xs text-gold-500">分类</Label>
                                        <Input value={r.category} onChange={(e) => update(i, { category: e.target.value })} placeholder="项链、耳环..." />
                                    </div>
                                    <div>
                                        <Label className="md:hidden text-xs text-gold-500">库存</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={r.stock}
                                            onChange={(e) => update(i, { stock: e.target.value })}
                                            className="font-mono text-right"
                                        />
                                    </div>
                                    <div>
                                        <Label className="md:hidden text-xs text-gold-500">成本 (RM)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={r.cost_price}
                                            onChange={(e) => update(i, { cost_price: e.target.value })}
                                            className="font-mono text-right"
                                        />
                                    </div>
                                    <div>
                                        <Label className="md:hidden text-xs text-gold-500">售价 (RM)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={r.unit_price}
                                            onChange={(e) => update(i, { unit_price: e.target.value })}
                                            className="font-mono text-right"
                                        />
                                    </div>
                                    <div>
                                        <Label className="md:hidden text-xs text-gold-500">阈值</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={r.low_stock_threshold}
                                            onChange={(e) => update(i, { low_stock_threshold: e.target.value })}
                                            className="font-mono text-right"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1 flex md:block gap-1 md:pt-0.5 justify-end">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => dupRow(i)}
                                            className="text-slate-400 hover:text-slate-700 h-9 w-9 p-0"
                                            title="复制此行"
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeRow(i)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-9 w-9 p-0"
                                            title="删除此行"
                                            data-testid={`bulk-remove-${i}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex items-center justify-between pt-1">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addRow}
                            data-testid="bulk-add-row-btn"
                            className="gap-1.5 border-dashed"
                        >
                            <Plus className="h-3.5 w-3.5" /> 再加一行
                        </Button>
                        <div className="text-xs font-mono">
                            <span className="text-gold-700">✓ {validCount} 件有效</span>
                            <span className="text-slate-400 ml-3">共 {rows.length} 行</span>
                        </div>
                    </div>
                </div>

                <DialogFooter className="pt-4">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
                    <Button
                        data-testid="submit-bulk-btn"
                        onClick={submit}
                        disabled={submitting || validCount === 0}
                        className="bg-gold-600 hover:bg-gold-700 text-white gap-2"
                    >
                        <Upload className="h-4 w-4" />
                        {submitting ? "添加中..." : `添加 ${validCount} 件产品`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
